import { lazy, Suspense } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import {
  ControlPanel,
  Header,
  Icon,
  LastCrashScores,
  RoundsDetails,
} from "../../components";
import { Loader } from "../../components/Loader/Loader";
import { preloadPioneerGame, preloadTwinsGame } from "../../games/preload";
import { useModal } from "../../shared/contexts";
import { useDebugTools } from "../../shared/hooks";
import { useIsMobile } from "../../shared/hooks/useIsMobile";
import { useAssetsStore } from "../../store/assetsStore";
import { useSkinStore } from "../../store/skinStore";
import css from "./HomeGame.module.scss";
import { useGameStatus } from "./hooks/useGameStatus";
import { useResize } from "./hooks/useResize";

const GAME_TYPE = import.meta.env.VITE_GAME_TYPE || "twins";

const PioneerGame = lazy(() =>
  preloadPioneerGame().then((m) => ({
    default: m.PioneerGame,
  }))
);

const TwinsGame = lazy(() =>
  preloadTwinsGame().then((m) => ({
    default: m.TwinsGame,
  }))
);
export const HomeGame = () => {
  const currentSkinId = useSkinStore((s) => s.skinIds[GAME_TYPE]);

  const {
    canvasWidth,
    canvasHeight,
    headerRef,
    scoresRef,
    controllersRef,
    gameContainerRef,
    arrowRef,
    isFullyLoading,
    isWaitingCanvasSize,
    isOpenDetails,
    isAndroid,
    setIsGameLoading,
    setIsOpenDetails,
  } = useResize();

  const { setForcedCrashPoint, disableForcedCrashPoint } = useDebugTools();
  const [crashPoint, setCrashPoint] = useState(1);
  const {
    gameState,
    multiplier,
    countdown,
    countdownTotal,
    history,
    missedWindows,
  } = useGameStatus();

  const { openModal } = useModal();

  const isMobile = useIsMobile();

  const handleOpenDetails = () => {
    setIsOpenDetails((prev) => !prev);
  };
  const [isTest, setIsTest] = useState(true);

  // Ассеты греются на входе в приложение (см. `useAssetsStore`), пока открыт
  // экран выбора комнаты. Здесь только зеркалим готовность в loading-стейт.
  const isAssetsReady = useAssetsStore((state) => state.isReady);

  useEffect(() => {
    setIsGameLoading(!isAssetsReady);
  }, [isAssetsReady, setIsGameLoading]);

  useEffect(() => {
    if (missedWindows >= 3) {
      openModal("reloadModal");
    }
  }, [missedWindows]);

  return (
    <div>
      {isFullyLoading ? <Loader /> : null}
      <div style={{ visibility: isFullyLoading ? "hidden" : "visible" }}>
        <Header
          headerRef={headerRef}
          isStickyActive={!isMobile || isOpenDetails}
        />

        <div
          className={`${css.main} ${isAndroid && isMobile ? css.androidPad : ""}`}
        >
          <div className={css.animationContainer} ref={gameContainerRef}>
            <LastCrashScores scoresRef={scoresRef} history={history} />

            <div
              className={css.gameWrapper}
              style={{
                minHeight: canvasHeight ? `${canvasHeight}px` : undefined,
              }}
            >
              {!isWaitingCanvasSize && canvasWidth && (
                <Suspense fallback={<Loader variant="overlay" />}>
                  {GAME_TYPE === "pioneer" ? (
                    <PioneerGame
                      key={`pioneer-skin-${currentSkinId}`}
                      multiplier={multiplier}
                      countdown={countdown}
                      countdownTotal={countdownTotal}
                      gameState={gameState}
                      onAnimationComplete={() => {}}
                      canvasWidth={canvasWidth}
                      canvasHeight={canvasHeight}
                    />
                  ) : (
                    <TwinsGame
                      key={`twins-skin-${currentSkinId}`}
                      multiplier={multiplier}
                      countdown={countdown}
                      countdownTotal={countdownTotal}
                      gameState={gameState}
                      onAnimationComplete={() => {}}
                      canvasWidth={canvasWidth}
                      canvasHeight={canvasHeight}
                    />
                  )}
                </Suspense>
              )}
            </div>

            <div className={css.controllers} ref={controllersRef}>
              {isTest ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <input
                    type="number"
                    value={crashPoint}
                    onChange={(e) =>
                      setCrashPoint(
                        Number((e.target as HTMLInputElement).value)
                      )
                    }
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                    }}
                  >
                    <button onClick={() => setForcedCrashPoint(crashPoint)}>
                      Краш
                    </button>
                    <button onClick={() => disableForcedCrashPoint()}>
                      Выключить
                    </button>
                    <button onClick={() => setIsTest(false)}>Скрыть</button>
                  </div>
                </div>
              ) : null}

              <ControlPanel
                multiplier={multiplier}
                gameState={gameState}
                slotId={1}
              />
              <ControlPanel
                multiplier={multiplier}
                gameState={gameState}
                slotId={2}
              />
            </div>
          </div>

          <div className={css.leftPanel}>
            <div
              className={css.btnContainer}
              onClick={handleOpenDetails}
              ref={arrowRef}
            >
              <div className={css.arrow}>
                <Icon
                  name="triangle"
                  className={`${css.triangle} ${isOpenDetails ? css.arrowUp : ""}`}
                />
              </div>
            </div>
            {isOpenDetails && <RoundsDetails />}
          </div>
        </div>
      </div>
    </div>
  );
};
